/**
 * DemocratCharts — Fibonacci helpers.
 *
 * Two patterns:
 *   - Retracement: pick a high and low (swing), draw levels between them.
 *     Standard levels: 0, 23.6, 38.2, 50, 61.8, 78.6, 100, plus extensions
 *     to 161.8 / 261.8 above (or below for downtrends).
 *
 *   - Extension: 3-point — start, end, retracement anchor. Projects where
 *     a follow-up move might end based on the original leg's length.
 *     Levels: 0 (retrace anchor), 61.8%, 100%, 161.8%, 261.8% in the
 *     direction away from the retracement.
 */

export const FIB_RETRACEMENT_LEVELS = [
  { ratio: 0,     label: "0",     color: "#787b86" },
  { ratio: 0.236, label: "0.236", color: "#4fc3f7" },
  { ratio: 0.382, label: "0.382", color: "#26a69a" },
  { ratio: 0.5,   label: "0.5",   color: "#ffb74d" },
  { ratio: 0.618, label: "0.618", color: "#f06292" },
  { ratio: 0.786, label: "0.786", color: "#ba68c8" },
  { ratio: 1,     label: "1",     color: "#787b86" },
  { ratio: 1.618, label: "1.618", color: "#9575cd" },
  { ratio: 2.618, label: "2.618", color: "#5c6bc0" },
] as const;

export const FIB_EXTENSION_LEVELS = [
  { ratio: 0,     label: "0",     color: "#787b86" },
  { ratio: 0.618, label: "0.618", color: "#f06292" },
  { ratio: 1,     label: "1",     color: "#26a69a" },
  { ratio: 1.272, label: "1.272", color: "#ffb74d" },
  { ratio: 1.618, label: "1.618", color: "#9575cd" },
  { ratio: 2.618, label: "2.618", color: "#5c6bc0" },
  { ratio: 4.236, label: "4.236", color: "#4fc3f7" },
] as const;

/**
 * Compute fib retracement levels between two prices.
 * @param p0 Anchor 1 price (typically the swing high if drawing top-down)
 * @param p1 Anchor 2 price (the swing low)
 * @returns Array of { level, price, label, color }
 */
export function computeFibRetracement(p0: number, p1: number) {
  const span = p1 - p0;
  return FIB_RETRACEMENT_LEVELS.map((l) => ({
    ratio: l.ratio,
    price: p0 + span * l.ratio,
    label: l.label,
    color: l.color,
  }));
}

/**
 * Compute fib extension levels.
 * @param p0 First point of the impulsive leg
 * @param p1 End of the impulsive leg
 * @param p2 Retracement anchor — where price pulled back to before extending
 * Levels are projected from p2 in the direction of (p1 - p0).
 */
export function computeFibExtension(p0: number, p1: number, p2: number) {
  const leg = p1 - p0;
  return FIB_EXTENSION_LEVELS.map((l) => ({
    ratio: l.ratio,
    price: p2 + leg * l.ratio,
    label: l.label,
    color: l.color,
  }));
}
